import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, Store, Trash2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * MerchantLogoUploader — square rounded preview + upload/remove controls.
 *
 * Uses the shared /api/uploads/merchant-logo endpoint (admin + merchant).
 *
 * Props:
 *   value:    current logo URL (or null)
 *   onChange: (url | null) => void, called after upload / removal
 *   size:     "sm" (40px, inline use) | "md" (80px, default)
 *   testId:   optional prefix for data-testid
 */
export default function MerchantLogoUploader({
  value,
  onChange,
  size = "md",
  testId = "merchant-logo",
}) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const dim = size === "sm" ? 40 : 80;
  const iconDim = size === "sm" ? "h-4 w-4" : "h-6 w-6";

  async function upload(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/merchant-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(r.data.url);
      toast.success(t("common.save"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="inline-flex items-center gap-2">
      <div
        className="relative inline-flex items-center justify-center rounded-xl border border-border bg-muted/40 overflow-hidden shrink-0"
        style={{ width: dim, height: dim }}
        data-testid={`${testId}-preview`}
      >
        {value ? (
          <img
            src={value}
            alt="logo"
            className="w-full h-full object-cover"
          />
        ) : (
          <Store className={`${iconDim} text-muted-foreground/60`} />
        )}
        {uploading && (
          <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
            <Loader2 className={`${iconDim} animate-spin text-foreground`} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <label
          className={`inline-flex items-center gap-1 rounded-lg border border-border px-2.5 h-8 cursor-pointer hover:bg-muted/40 text-xs font-medium ${
            uploading ? "opacity-60 pointer-events-none" : ""
          }`}
          data-testid={`${testId}-upload-label`}
        >
          <Upload className="h-3 w-3" />
          <span>{value ? t("common.edit") : t("common.upload")}</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`${testId}-input`}
          />
        </label>
        {value && (
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className="h-8 w-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center"
            data-testid={`${testId}-remove`}
            title={t("common.delete")}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
